import React, { FC, useContext, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router';
import Login from './pages/Login';
import { QueryClient, QueryClientProvider } from 'react-query';
import ProtectedRoute from './components/ProtectedRoute';
import Archive from './pages/Archive';
import Home from './pages/Home';
import { AuthUser } from './models/user';
import { UserContext } from '.';
import {
  REMEMBER_ME_LOCAL_STORAGE_KEY,
  SESSION_STORAGE_ACTIVE_KEY,
} from './pages/Login/constants';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ROLE_ADMIN } from './constants';
import UsersList from './pages/UsersList';
import Profile from './pages/Profile';
import ViewDocument from './pages/Document/View';
import RetrievePassword from './pages/RetrievePassword';
import Verify from './pages/Verify';
import AdminEntities from './pages/AdminEntities';
import { readSessionUser } from './utils/session';
import ErrorScreen from './pages/ErrorScreen';
import Search from './pages/Search';
import SearchResults from './pages/Search/SearchResults';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline/CssBaseline';
import theme from './theme'; // your theme file

// Create a client
const queryClient = new QueryClient();

const AppWithRoutes: FC = () => {
  const userContext = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      ['/', '/login', '/retrieve-password', '/404'].includes(
        location.pathname,
      ) ||
      location.pathname.startsWith('/verify/')
    ) {
      return;
    } else {
      const shouldRememberMe = localStorage.getItem(
        REMEMBER_ME_LOCAL_STORAGE_KEY,
      );
      const isSessionActive = sessionStorage.getItem(
        SESSION_STORAGE_ACTIVE_KEY,
      );

      if (shouldRememberMe === 'true' || isSessionActive === 'true') {
        // Rebuilt from the token rather than from localStorage: the stored account is
        // editable by whoever owns the browser, and its `role` used to be enough to
        // open the administration screens. See utils/session.
        const sessionUser = readSessionUser();

        if (sessionUser) {
          // Only the user is restored here. Navigating back to the requested path would
          // undo the guards: they redirect to "/", and this effect would send the
          // visitor straight back to the route that just refused them, which showed as
          // an empty page rather than a redirect. The router is already on the right
          // path, so there is nothing to navigate to.
          userContext?.updateUser(sessionUser);

          if (location.pathname === '/') {
            navigate('/my-documents');
          }
        } else {
          // Remembered account, no usable token: nothing to restore.
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      <Route path="/404" element={<ErrorScreen code={404} />} />
      {/* Public: anyone scanning a document's QR code lands here, without an account. */}
      <Route path="/verify/:code" element={<Verify />} />
      {/* The XD Home is a single screen (pages/Home), which replaced the
          former MyDocuments and AllDocuments (now removed):
          "/my-documents" is the Home with the "Archiveur" filter set to the
          current user. */}
      <Route
        path="/"
        element={
          sessionStorage.getItem(SESSION_STORAGE_ACTIVE_KEY) &&
          sessionStorage.getItem(SESSION_STORAGE_ACTIVE_KEY) === 'true' ? (
            <Home />
          ) : (
            <Login />
          )
        }
      />
      {/* Once signed in, Login navigates to "/my-documents". That navigation is a
          transition: the user is set first and this route renders once more before
          the new one replaces it. It used to render the Home here, which then gave way
          to a second Home on "/my-documents": the listing was requested twice, and a
          field picked from the first one was gone from the page a moment later. It
          redirects instead, so the Home is only ever mounted on its own route. */}
      <Route
        path="/login"
        element={
          sessionStorage.getItem(SESSION_STORAGE_ACTIVE_KEY) &&
          sessionStorage.getItem(SESSION_STORAGE_ACTIVE_KEY) === 'true' ? (
            <Navigate to="/my-documents" replace />
          ) : (
            <Login />
          )
        }
      />
      {/* "/profile" is the Home with the user's own record open
          (pages/Profile). It used to be the only authenticated route without a
          guard (ROADMAP.md, 2.6). */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-documents"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <Home scope="all" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-documents"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <Home scope="me" />
          </ProtectedRoute>
        }
      />
      {/* XD screens 6 and 7: "/search" is the Home with the "Chercher
          un document" modal open (pages/Search), and "/search/results" the
          results screen. The criteria travel in the query string. */}
      <Route
        path="/search"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <Search />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search/results"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <SearchResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/document/:id"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <ViewDocument />
          </ProtectedRoute>
        }
      />
      {/* "/document/edit/:id" is the record with the "Modifier le document"
          modal open (pages/Document/View with `editing`); the same component
          on both routes, so that moving from one to the other does not reload
          the document. */}
      <Route
        path="/document/edit/:id"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <ViewDocument editing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/archive"
        element={
          <ProtectedRoute user={userContext ? userContext.mainUser : null}>
            <Archive />
          </ProtectedRoute>
        }
      />
      {/* The two-page flow (Archive → Step2With/WithoutQRCode, now removed)
          is now the "/archive" modal. The old routes redirect there, in case
          someone has them bookmarked. */}
      <Route path="/archive-secured" element={<Navigate to="/archive" replace />} />
      <Route path="/archive-unsecured" element={<Navigate to="/archive" replace />} />
      {/* XD screens 8 and 9 are one: "/create-user", "/edit-user/:id"
          and "/view-user/:id" are the users list with the create, edit or
          view modal open (see pages/UsersList). The routes are kept for the
          guards, the "Ajouter un utilisateur" tile, the name link in the
          table and the specs. */}
      <Route
        path="/create-user"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <UsersList dialog="create" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users-list"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <UsersList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <AdminEntities kind="service" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/functions"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <AdminEntities kind="function" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit-user/:id"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <UsersList dialog="edit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/view-user/:id"
        element={
          <ProtectedRoute
            user={
              userContext && userContext.mainUser?.role === ROLE_ADMIN
                ? userContext.mainUser
                : null
            }
          >
            <UsersList dialog="view" />
          </ProtectedRoute>
        }
      />
      <Route path="/retrieve-password/*" element={<RetrievePassword />} />
      <Route path="/500" element={<ErrorScreen code={500} />} />
      <Route path="*" element={<ErrorScreen code={404} />} />
    </Routes>
  );
};

const App: FC = () => {
  // Read once, before the first render: ProtectedRoute runs during that render, and
  // filling the user in from an effect leaves it looking at null for a pass, which
  // renders an empty page instead of redirecting.
  const [user, setUser] = useState<AuthUser | null>(() => readSessionUser());
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
        <QueryClientProvider client={queryClient}>
          <UserContext.Provider
            value={{
              mainUser: user,
              updateUser: (arg: AuthUser | null) => setUser(arg),
            }}
          >
            <BrowserRouter>
              <AppWithRoutes />
            </BrowserRouter>
          </UserContext.Provider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
